import { CrossmintEvent, MintConfig, clientNames, crossmintIFrameService, useCrossmintEvents } from "@crossmint/client-sdk-base";
import { Redirector } from "../tools/Redirector";
import { PaymentUrlResolver } from "./utils/PaymentUrlResolver";
import { ILogger } from "../cityversetycoon/logger/ILogger";
import { Web3Config } from "./config/Web3Config";
import { INetworkClient } from "../cityversetycoon/networking/INetworkClient";
import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { CrossmintPaymentResult } from "./CrossmintPaymentResult";
import { CancellationToken } from "async/CancellationToken";
import { IFrameManager } from "../tools/IFrameManager";
import { PaymentResult } from "./PaymentResult";
import { GameInfo, Stage } from "../analytics/GameInfo";
import { VariableSaver } from "../tools/variableSaver/VariableSaver";
import { CancellationTokenSource } from "../shared/async/CancellationTokenSource";
import { TickerDelay } from "../tools/TickerDelay";
import { ObjectFieldsChecker } from "../tools/ObjectFieldsChecker";

type IFramePaymentOptions = {
  address: string,
  collectionId: string,
  mintConfig: MintConfig
}

export class CrossmintSdk {
  private static readonly PAYMENT_TIMEOUT = 4000;
  private static readonly PAYMENT_RESULT_KEY = "cross_sdk_result";
  private static readonly PAYMERT_ERROR_KEY = "cross_sdk_error";

  private readonly web3Config: Web3Config;
  private readonly networkClient: INetworkClient;
  private readonly paymentUrlResolver: PaymentUrlResolver;
  private readonly variableSaver: VariableSaver;
  private readonly logger: ILogger;
  private readonly embededCheckout: boolean;

  private timeoutCancellationTokenSource: CancellationTokenSource;

  public constructor(
    web3Config: Web3Config,
    gameInfo: GameInfo,
    networkClient: INetworkClient,
    paymentUrlResolver: PaymentUrlResolver,
    variableSaver: VariableSaver,
    logger: ILogger
  ) {
    this.web3Config = web3Config;
    this.variableSaver = variableSaver;
    this.networkClient = networkClient;
    this.paymentUrlResolver = paymentUrlResolver;
    this.logger = logger;
    this.embededCheckout = false;//gameInfo.serverStage === Stage.DEVELOPMENT;
  }

  public async paymentBuildingWithMint(buildingId: string, cancellationToken: CancellationToken, couponBenefitIds?: Array<string>): Promise<CrossmintPaymentResult> {
    if (!this.embededCheckout) {
      const url = this.paymentUrlResolver.resolveCheckoutUrlWithMintBackendRequest(
        buildingId,
        this.web3Config.crossmintBuildingWithMintCollectionId,
        this.web3Config.crossmintProjectId,
        clientNames.vanillaUi,
        this.web3Config.crossmintEnvironment,
        "1.1.6",
        "usd",
        "en-US",
        `${location.origin}/crossmint/`,
        `${location.origin}/crossmint/`,
        "",
        undefined,
        undefined,
        undefined,
        "fiat",
        "metamask",
        undefined,
        {
          delivery: "non-custodial",
          display: "new-tab",
          experimental: true,
          paymentMethods: ["fiat", "ETH"]
        },
        couponBenefitIds
      );
      this.logger.debug("url: " + url);
      Redirector.openPageInNewTab(url);
      return await this.waitPayment(cancellationToken);
    }

    const mintDto = await this.networkClient.mpPrepareMint(buildingId, couponBenefitIds);

    return await this.iframePayment({
      mintConfig: {
        totalPrice: mintDto.totalPrice,
        _signature: mintDto.signature,
        _req: {
          to: mintDto.mintRequest.to,
          royaltyRecipient: mintDto.mintRequest.royaltyRecipient,
          royaltyBps: mintDto.mintRequest.royaltyBps,
          primarySaleRecipient: mintDto.mintRequest.primarySaleRecipient,
          uri: mintDto.mintRequest.uri,
          price: mintDto.mintRequest.price,
          currency: mintDto.mintRequest.currency,
          uid: mintDto.mintRequest.uid,
          validityStartTimestamp: mintDto.mintRequest.validityStartTimestamp,
          validityEndTimestamp: mintDto.mintRequest.validityEndTimestamp,
        }
      },
      address: mintDto.mintRequest.to,
      collectionId: this.web3Config.crossmintBuildingWithMintCollectionId
    });
  }

  public async paymentBuildingWithoutMint(address: string, tokenId: string, cancellationToken: CancellationToken): Promise<CrossmintPaymentResult> {
    if (!this.embededCheckout) {
      const url = this.paymentUrlResolver.resolveCheckoutUrl(
        this.web3Config.crossmintBuildingCollectionId,
        this.web3Config.crossmintProjectId,
        clientNames.vanillaUi,
        this.web3Config.crossmintEnvironment,
        "1.1.6",
        "usd",
        "en-US",
        {
          type: "reservoir-secondary-eth",
          contractAddress: this.web3Config.crossmintBuildingContractAddress,
          tokenId: tokenId
        },
        `${location.origin}/crossmint/`,
        `${location.origin}/crossmint/`,
        address,
        undefined,
        undefined,
        undefined,
        "fiat",
        "metamask",
        undefined,
        {
          delivery: "non-custodial",
          display: "new-tab",
          experimental: true,
          paymentMethods: ["fiat", "ETH"]
        }
      );
      this.logger.debug("url: " + url);
      Redirector.openPageInNewTab(url);
      return await this.waitPayment(cancellationToken);
    }

    return await this.iframePayment({
      mintConfig: {
        type: "reservoir-secondary-eth",
        contractAddress: this.web3Config.crossmintBuildingContractAddress,
        tokenId: tokenId
      },
      address,
      collectionId: this.web3Config.crossmintBuildingCollectionId
    });
  }

  public async storePayment(address: string, tokenId: string, price: number, cancellationToken: CancellationToken): Promise<CrossmintPaymentResult> {
    if (!this.embededCheckout) {
      const url = this.paymentUrlResolver.resolveCheckoutUrl(
        this.web3Config.crossmintStoreCollectionId,
        this.web3Config.crossmintProjectId,
        clientNames.vanillaUi,
        this.web3Config.crossmintEnvironment,
        "1.1.6",
        "usd",
        "en-US",
        {
          totalPrice: price.toString(),
          quantity: 1,
          tokenId: tokenId,
        },
        `${location.origin}/crossmint/`,
        `${location.origin}/crossmint/`,
        address,
        undefined,
        undefined,
        undefined,
        "fiat",
        "metamask",
        undefined,
        {
          delivery: "non-custodial",
          display: "new-tab",
          experimental: true,
          paymentMethods: ["fiat", "ETH"]
        }
      );
      this.logger.debug("url: " + url);

      Redirector.openPageInNewTab(url);
      return await this.waitPayment(cancellationToken);
    }

    return await this.iframePayment({
      mintConfig: {
        totalPrice: price.toString(),
        quantity: 1,
        tokenId: tokenId,
        type: "erc-1155"
      },
      address,
      collectionId: this.web3Config.crossmintStoreCollectionId
    });
  }

  public async successPayment(transactionHash: string, tokenId: string): Promise<void> {
    await this.networkClient.successPurchaseBuilding(transactionHash, tokenId);
  }

  private async waitPayment(operationCancellationToken: CancellationToken): Promise<CrossmintPaymentResult> {
    const paymentPCS = new PromiseCompletionSource<CrossmintPaymentResult>();

    operationCancellationToken.onCanceled.once(() => {
      handleError(new CrossmintPaymentTimeout());
    });

    const handleResult = (result: CrossmintPaymentResult) => {
      document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
      this.clearTimeoutCS();
      this.variableSaver.removeItem(CrossmintSdk.PAYMENT_RESULT_KEY);
      this.variableSaver.removeItem(CrossmintSdk.PAYMERT_ERROR_KEY);
      paymentPCS.complete(result);
    }

    const handleError = (error: Error) => {
      document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
      this.clearTimeoutCS();
      this.variableSaver.removeItem(CrossmintSdk.PAYMENT_RESULT_KEY);
      this.variableSaver.removeItem(CrossmintSdk.PAYMERT_ERROR_KEY);
      paymentPCS.error(error);
    }

    const onDocumentVisibilityChange = async () => {
      if (document.hidden) {
        this.clearTimeoutCS();
        return;
      }

      if (this.variableSaver.exist(CrossmintSdk.PAYMENT_RESULT_KEY)) {
        const paymentResult = this.variableSaver.getString(CrossmintSdk.PAYMENT_RESULT_KEY);
        if (paymentResult != null) {
          handleResult(this.createCrossmintPaymentResult(paymentResult));
          return;
        }
      }

      if (this.variableSaver.exist(CrossmintSdk.PAYMERT_ERROR_KEY)) {
        const paymentError = this.variableSaver.getString(CrossmintSdk.PAYMERT_ERROR_KEY);
        if (paymentError != null) {
          handleError(new Error(paymentError));
          return;
        }
      }

      if (this.timeoutCancellationTokenSource == null) {
        this.timeoutCancellationTokenSource = new CancellationTokenSource();
      }

      const source = this.timeoutCancellationTokenSource;
      const token = source.token;
      await TickerDelay.delay(CrossmintSdk.PAYMENT_TIMEOUT);

      if (!token.isCancellationRequested) {
        if (this.variableSaver.exist(CrossmintSdk.PAYMENT_RESULT_KEY)) {
          const paymentResult = this.variableSaver.getString(CrossmintSdk.PAYMENT_RESULT_KEY);
          if (paymentResult != null) {
            handleResult(this.createCrossmintPaymentResult(paymentResult));
            return;
          }
        }

        if (this.variableSaver.exist(CrossmintSdk.PAYMERT_ERROR_KEY)) {
          const paymentError = this.variableSaver.getString(CrossmintSdk.PAYMERT_ERROR_KEY);
          if (paymentError != null) {
            handleError(new Error(paymentError));
            return;
          }
        }

        handleError(new CrossmintPaymentTimeout());
      }

      source?.dispose();
      if (this.timeoutCancellationTokenSource === source) {
        this.timeoutCancellationTokenSource = null;
      }
    }

    document.addEventListener('visibilitychange', onDocumentVisibilityChange);
    return await paymentPCS.result;
  }

  private clearTimeoutCS(): void {
    this.timeoutCancellationTokenSource?.cancel();
    this.timeoutCancellationTokenSource?.dispose();
    this.timeoutCancellationTokenSource = null;
  }

  private createCrossmintPaymentResult(payment: string): CrossmintPaymentResult {
    try {
      const crossmintPaymentResult: CrossmintPaymentResult = JSON.parse(payment)?.[0];
      ObjectFieldsChecker.throwIfInvalidFields(crossmintPaymentResult, ["status", "txId", "tokenId"]);
      return crossmintPaymentResult;
    } catch (error: unknown) {
      this.logger.error(error);
      return null;
    }
  }

  private async iframePayment(options: IFramePaymentOptions): Promise<CrossmintPaymentResult> {
    const pcs = new PromiseCompletionSource<CrossmintPaymentResult>();
    let clearEventsListener: () => void = null;
    try {
      const { getUrl, listenToEvents } = crossmintIFrameService({
        clientId: options.collectionId,
        projectId: this.web3Config.crossmintProjectId,
        environment: this.web3Config.crossmintEnvironment,
        currency: "usd",
        collectionId: options.collectionId,
        locale: "en-US",
        paymentMethod: "fiat",
        emailInputOptions: {
          show: true
        }
      });

      clearEventsListener = listenToEvents(async (event: MessageEvent<CrossmintEvent>) => {
        switch (event.data.type) {
          case "payment:process.started":
            this.logger.debug("Payment started");
            break;
          case "payment:preparation.failed":
            pcs.error(new Error("Payment failed: payment:preparation.failed raised"));
            break;
          case "payment:process.succeeded":
            const orderIdentifier = event.data.payload.orderIdentifier;
            IFrameManager.closeIFrame();
            this.logger.debug(`Payment succeeded, order id: ${orderIdentifier}`);
            const mintResult = await this.mint(orderIdentifier);
            if (mintResult == null) {
              pcs.error(new Error("Payment failed, mint result not defined"));
              break;
            }
            pcs.complete({
              status: PaymentResult.SUCCESS,
              tokenId: mintResult.tokenId,
              txId: mintResult.txId
            });
            break;
          case "payment:process.rejected":
            this.logger.debug(`Payment rejected, ${event.data.payload.error.message}`);
            pcs.complete({ status: PaymentResult.FAILURE, tokenId: null, txId: null });
            break;
        }
      });

      const url = getUrl({
        clientId: options.collectionId,
        collectionId: options.collectionId,
        projectId: this.web3Config.crossmintProjectId,
        currency: "usd",
        locale: "en-US",
        environment: this.web3Config.crossmintEnvironment,
        mintConfig: options.mintConfig,
        paymentMethod: "fiat",
        recipient: {
          wallet: options.address
        },
        emailInputOptions: {
          show: true
        }
      });
      IFrameManager.openInIFrame(url);
      return await pcs.result;
    }
    catch (e: unknown) {
      this.logger.error("Exception on processing iframe payment: ", e);
      return { status: PaymentResult.FAILURE, tokenId: null, txId: null };
    }
    finally {
      clearEventsListener?.();
    }
  }

  private async mint(orderIdentifier: string): Promise<MintResult> {
    const pcs = new PromiseCompletionSource<MintResult>();
    let clearMintingEventsListener: () => void = null;
    try {
      const { listenToMintingEvents } = useCrossmintEvents({
        environment: this.web3Config.crossmintEnvironment
      });

      const { cleanup } = listenToMintingEvents({ orderIdentifier }, (event: CrossmintEvent) => {
        console.log("minting event: ", event);
        switch (event.type) {
          case "transaction:fulfillment.succeeded":
            pcs.complete({ tokenId: (event.payload as any).tokenIds[0], txId: event.payload.txId });
            break;
          case "transaction:fulfillment.failed":
            break;
          default:
            break;
        }
      });
      clearMintingEventsListener = cleanup;
      return await pcs.result;
    }
    catch (e: unknown) {
      this.logger.error("Exception on mint: ", e);
      return null;
    }
    finally {
      clearMintingEventsListener?.();
    }
  }
}

type MintResult = {
  tokenId: string,
  txId: string
}

export class CrossmintPaymentTimeout extends Error {
  public constructor() {
    super("Crossmint payment timeout");
  }
}